import React, { useState } from 'react';
import { Button } from '../components/Button';
import { Calendar, CheckCircle2, Bug } from 'lucide-react';
import { NonMondayStartModal } from '../components/NonMondayStartModal';
import { TrainingCycleAlignmentModal } from '../components/TrainingCycleAlignmentModal';
interface ChooseStartDateProps {
  onConfirm: (mode?: 'keep' | 'align') => void;
}
export function ChooseStartDate({
  onConfirm
}: ChooseStartDateProps) {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showNonMondayModal, setShowNonMondayModal] = useState(false);
  const [showAlignmentModal, setShowAlignmentModal] = useState(false);
  const [alignmentPreselected, setAlignmentPreselected] = useState<'next' | 'align' | undefined>(undefined);
  const [showDebug, setShowDebug] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const isMonday = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.getDay() === 1;
  };
  const handleConfirm = () => {
    if (selectedDate && !isMonday(selectedDate)) {
      setShowAlignmentModal(true);
    } else {
      onConfirm('keep');
    }
  };
  return <div className="flex flex-col h-full w-full relative overflow-hidden bg-stone-100" data-id="element-445">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-[60] w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-446">
        <Bug size={14} data-id="element-447" />
      </button>

      {showDebug && <div className="absolute top-16 right-2 z-[60] bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-56 text-xs animate-in fade-in zoom-in-95 duration-150" data-id="element-448">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-449">
            Debug Panel
          </p>
          <button onClick={() => {
        setShowNonMondayModal(true);
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-450">
            ▶ Open non-Monday warning (old)
          </button>
          <button onClick={() => {
        setAlignmentPreselected(undefined);
        setShowAlignmentModal(true);
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-451">
            ▶ Open date alignment pop-up
          </button>
          <button onClick={() => {
        setAlignmentPreselected('next');
        setShowAlignmentModal(true);
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-452">
            ▶ Alignment — Option A selected
          </button>
          <button onClick={() => {
        setAlignmentPreselected('align');
        setShowAlignmentModal(true);
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-453">
            ▶ Alignment — Option B selected
          </button>
        </div>}

      {/* Blurred Dashboard Background Mockup */}
      <div className="absolute inset-0 opacity-40 blur-sm pointer-events-none flex flex-col p-6 space-y-6" data-id="element-454">
        <div className="flex justify-between items-center mt-8" data-id="element-455">
          <div className="w-32 h-8 bg-slate-300 rounded-lg" data-id="element-456"></div>
          <div className="w-12 h-12 bg-slate-300 rounded-full" data-id="element-457"></div>
        </div>
        <div className="w-full h-32 bg-slate-300 rounded-3xl" data-id="element-458"></div>
        <div className="w-full h-48 bg-slate-300 rounded-3xl" data-id="element-459"></div>
      </div>

      {/* Modal Overlay */}
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm z-10 flex items-end sm:items-center justify-center p-4" data-id="element-460">
        <div className="bg-white w-full max-w-sm rounded-[2.5rem] p-6 shadow-2xl animate-in slide-in-from-bottom-8 fade-in duration-300" data-id="element-461">
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-6 mx-auto" data-id="element-462">
            <Calendar size={24} data-id="element-463" />
          </div>

          <div className="text-center mb-6" data-id="element-464">
            <h2 className="text-2xl font-extrabold text-slate-800 mb-2" data-id="element-465">
              When do we start?
            </h2>
            <p className="text-slate-500 font-medium text-sm leading-relaxed" data-id="element-466">
              Your program is ready. Pick a start date to schedule your
              workouts.
            </p>
          </div>

          <div className="space-y-5 mb-8" data-id="element-467">
            <div className="bg-stone-50 p-4 rounded-2xl border border-stone-100" data-id="element-468">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block" data-id="element-469">
                Select Start Date
              </label>
              <input type="date" min={today} value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-full bg-white border-2 border-slate-200 focus:border-emerald-400 hover:border-slate-300 rounded-xl py-3 px-4 text-slate-800 font-bold focus:outline-none transition-colors" data-id="element-470" />
            </div>

            <div className="flex items-start gap-3 px-2" data-id="element-471">
              <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={18} data-id="element-472" />
              <p className="text-sm text-slate-600 font-medium" data-id="element-473">
                You selected to train on{' '}
                <span className="font-bold text-slate-800" data-id="element-474">
                  Tuesdays, Thursdays, and Fridays
                </span>
                .
              </p>
            </div>
          </div>

          <Button variant="primary" fullWidth onClick={handleConfirm} disabled={!selectedDate} data-id="element-475">
            Confirm start date
          </Button>
        </div>
      </div>

      {/* Non-Monday Start Modal (Old) */}
      {showNonMondayModal && <NonMondayStartModal selectedDate={selectedDate} onKeepDate={() => {
      setShowNonMondayModal(false);
      onConfirm('keep');
    }} onAlignMonday={() => {
      setShowNonMondayModal(false);
      onConfirm('align');
    }} onClose={() => setShowNonMondayModal(false)} data-id="element-476" />}

      {/* Training Cycle Alignment Modal (New) */}
      {showAlignmentModal && <TrainingCycleAlignmentModal selectedDate="Wednesday, 25 March" nextWorkoutDate="Thursday, 26 March" alignedDate="Tuesday, 31 March" onStartNextWorkout={() => {
      setShowAlignmentModal(false);
      onConfirm('keep');
    }} onAlignToDay1={() => {
      setShowAlignmentModal(false);
      onConfirm('align');
    }} onClose={() => setShowAlignmentModal(false)} preselectedOption={alignmentPreselected} data-id="element-477" />}
    </div>;
}